import { ReportCustomTemplateField, ReportCustomTemplateFieldWithName, ReportTemplateTypes } from '../../api/dtos';
import type { AppColumn } from '../../components/tables/AppTable.types';

import {
    buildAssetsColumns,
    buildCommissionReportColumns,
    buildContractCancellationSummaryColumns,
    buildContractsOverdueColumns,
    buildGrossRealisationColumns,
    buildLotPricingComparisonColumns,
    buildLotSummaryColumns,
    buildSalesAdviceToContractColumns,
    buildSalesRateColumns,
    buildSalesSummaryColumns,
    buildStageLotCountsColumns,
    buildTemplateFieldColumns,
    buildUpcomingSettlementsColumns,
    type ReportColumnRow,
} from './ReportColumnDefinitions';

export interface ReportTableRows {
    rows: ReportColumnRow[];
    getRowId(row: ReportColumnRow): string | number;
}

/** Keeps actual domain IDs (including zero); fallback IDs are tied to source order. */
export function createReportTableRows(sourceRows: readonly ReportColumnRow[] = []): ReportTableRows {
    const fallbackIds = new WeakMap<ReportColumnRow, string>();
    const rows = [...sourceRows];

    rows.forEach((row, index) => fallbackIds.set(row, `report-row-${index}`));

    return {
        rows,
        getRowId: (row) => row.Id ?? row.id ?? fallbackIds.get(row) ?? 'report-row-unmapped',
    };
}

export function buildSavedReportColumns(
    reportType: ReportTemplateTypes | undefined,
    rows: readonly ReportColumnRow[],
    reportId: number,
    fields?: ReportCustomTemplateField[],
    fieldsWithNames?: ReportCustomTemplateFieldWithName[],
): AppColumn<ReportColumnRow>[] {
    switch (reportType) {
        case ReportTemplateTypes.Custom:
            return buildTemplateFieldColumns(fields, fieldsWithNames);
        case ReportTemplateTypes.SalesSummary:
            return buildSalesSummaryColumns(rows);
        case ReportTemplateTypes.ContractsOverdue:
            return buildContractsOverdueColumns();
        case ReportTemplateTypes.LotSummary:
            return buildLotSummaryColumns();
        case ReportTemplateTypes.CommissionReport:
            return buildCommissionReportColumns();
        case ReportTemplateTypes.SalesAdviceToContract:
            return buildSalesAdviceToContractColumns();
        case ReportTemplateTypes.GrossRealisationReport:
            return buildGrossRealisationColumns(rows);
        case ReportTemplateTypes.SalesRateReport:
            return buildSalesRateColumns(rows);
        case ReportTemplateTypes.StageLotCounts:
            return buildStageLotCountsColumns();
        case ReportTemplateTypes.UpcomingSettlements:
            return buildUpcomingSettlementsColumns();
        case ReportTemplateTypes.Assets:
            return buildAssetsColumns(`reports/preview/${reportId}`);
        case ReportTemplateTypes.LotPricingComparison:
            return buildLotPricingComparisonColumns();
        default:
            return [];
    }
}

export function buildTemplatePreviewColumns(
    templateType: ReportTemplateTypes | undefined,
    rows: readonly ReportColumnRow[],
    templateId: number,
    fields?: ReportCustomTemplateField[],
    fieldsWithNames?: ReportCustomTemplateFieldWithName[],
): AppColumn<ReportColumnRow>[] {
    switch (templateType) {
        case ReportTemplateTypes.Custom:
            return buildTemplateFieldColumns(fields, fieldsWithNames);
        case ReportTemplateTypes.SalesSummary:
            return buildSalesSummaryColumns(rows);
        case ReportTemplateTypes.ContractsOverdue:
            return buildContractsOverdueColumns();
        case ReportTemplateTypes.LotSummary:
            return buildLotSummaryColumns();
        case ReportTemplateTypes.CommissionReport:
            return buildCommissionReportColumns();
        case ReportTemplateTypes.SalesAdviceToContract:
            return buildSalesAdviceToContractColumns();
        case ReportTemplateTypes.GrossRealisationReport:
            return buildGrossRealisationColumns(rows);
        case ReportTemplateTypes.SalesRateReport:
            return buildSalesRateColumns(rows);
        case ReportTemplateTypes.StageLotCounts:
            return buildStageLotCountsColumns();
        case ReportTemplateTypes.UpcomingSettlements:
            return buildUpcomingSettlementsColumns();
        case ReportTemplateTypes.Assets:
            return buildAssetsColumns(`reports/templates/preview/${templateId}`);
        case ReportTemplateTypes.LotPricingComparison:
            return buildLotPricingComparisonColumns();
        case ReportTemplateTypes.ContractCancellationSummary:
            return buildContractCancellationSummaryColumns();
        default:
            return [];
    }
}
